<script setup>
// --------------------------------- Imports -------------------------------- //

// ---------------------------------- Props --------------------------------- //
const props = defineProps({
  columnConfig: {
    type: Object,
    default: () => ({}),
  },
  componentConfig: {
    type: Object,
    default: () => ({}),
  },
  filterConfig: {
    type: Object,
    default: () => ({}),
  },
  operatorConfig: {
    type: Object,
    default: () => ({}),
  },
  formData: {
    type: Object,
    default: null,
  },
});
// ---------------------------------- Emits --------------------------------- //

// ---------------------------- Injects/Provides ---------------------------- //

// ----------------------- Variables - Pinia - consts ----------------------- //

// --------------------- Variables - Pinia - storeToRefs -------------------- //

// ------------------- Variables - Local - consts and lets ------------------ //

// ------------------------ Variables - Local - refs ------------------------ //
const include_period_checkbox_text = ref('');
// ---------------------- Variables - Local - reactives --------------------- //

// --------------------------- Computed properties -------------------------- //
const anchor_period_options = computed(() => {
  const obj = props.operatorConfig?.options?.direction?.valueMapping || {};
  const result = Object.entries(obj).map(([key, value]) => ({
    label: key,
    value,
  }));
  return result;
});

const default_operator_name = computed(() => {
  return props.filterConfig?.operator_name || props.operatorConfig?.options?.direction?.valueMapping?.[props.operatorConfig.options.direction.default];
});

const default_anchor_period_time_unit = computed(() => {
  const default_val = props.formData?.operator_name === 'relative_date_current' ? 'week' : 'weeks';

  return props.filterConfig?.anchor_period_time_unit || default_val;
});

// -------------------------------- Functions ------------------------------- //
function onOperatorNameChange(newVal, _oldVal, el$) {
  if (newVal === 'relative_date_last' && !['today', 'custom_date'].includes(props.formData.anchor_period_starting_point)) {
    el$.form$.elements$.anchor_period_starting_point.update('earlier');
  }
  if (newVal === 'relative_date_next' && !['today', 'custom_date'].includes(props.formData.anchor_period_starting_point)) {
    el$.form$.elements$.anchor_period_starting_point.update('later');
  }

  generateIncludePeriodText();
}

function onAnchorPeriodStartingPointChange(newVal, _oldVal, el$) {
  if ((!props.formData.anchor_period_starting_amount && !props.formData.anchor_period_starting_time_unit) && ['earlier', 'later'].includes(newVal)) {
    el$.form$.elements$.anchor_period_starting_amount.update(props.formData.anchor_period_time_amount);
    el$.form$.elements$.anchor_period_starting_time_unit.update(props.formData.anchor_period_time_unit);
  }
}

function onAnchorPeriodTimeUnitChange() {
  generateIncludePeriodText();
}

function generateIncludePeriodText() {
  const val = props.formData?.anchor_period_time_unit || default_anchor_period_time_unit.value;

  // Handle special case for days/day
  if (val === 'days' || val === 'day') {
    include_period_checkbox_text.value = 'Include today';
    return;
  }

  // Create mapping for time units to their singular forms
  const timeUnitMapping = {
    // Plural forms
    weeks: 'week',
    months: 'month',
    quarters: 'quarter',
    years: 'year',
    // Singular forms (already correct)
    week: 'week',
    month: 'month',
    quarter: 'quarter',
    year: 'year',
  };

  const singularUnit = timeUnitMapping[val] || val;
  const text = `Include this ${singularUnit}`;

  include_period_checkbox_text.value = text;
}

if ((props.formData?.operator_name || default_operator_name.value) !== 'relative_date_current') {
  generateIncludePeriodText();
}
// -------------------------------- Watchers -------------------------------- //

// ----------------------------- Lifecycle Hooks ---------------------------- //
// console.log('Anchor Period', props);
</script>

<template>
  <div class="col-span-12">
    <RadiogroupElement
      name="operator_name"
      :items="anchor_period_options"
      :default="default_operator_name"
      class="capitalize"
      :add-classes="{
        RadiogroupElement: {
          wrapper: ['!flex-row', 'gap-6'],
        },
      }"
      @change="onOperatorNameChange"
    />
    <div class="flex gap-4 py-2">
      <TextElement
        name="anchor_period_time_amount"
        class="w-1/3"
        :default="props.filterConfig?.anchor_period_time_amount || 1"
        input-type="number"
        :rules="['integer', 'min:1']"
        :add-classes="{
          ElementError: {
            container: 'hidden',
          },
        }"
        autocomplete="off"
        :conditions="[['operator_name', '!=', 'relative_date_current']]"
        @keydown.space.stop
      />
      <SelectElement
        :key="props.formData?.operator_name"
        name="anchor_period_time_unit"
        class="capitalize w-full"
        :can-clear="false"
        :can-deselect="false"
        :default="default_anchor_period_time_unit"
        :native="false"
        :items="props.formData?.operator_name === 'relative_date_current' ? ['day', 'week', 'month', 'quarter', 'year'] : ['days', 'weeks', 'months', 'quarters', 'years']"
        @change="onAnchorPeriodTimeUnitChange"
      />
    </div>
    <div v-if="props.formData?.operator_name !== 'relative_date_current'">
      <div class="flex gap-2 items-center">
        <span class="text-xs text-gray-500 text-nowrap">Starting from</span>
        <TextElement
          name="anchor_period_starting_amount"
          class="w-1/3"
          :default="props.filterConfig?.anchor_period_starting_amount"
          input-type="number"
          :rules="['integer', 'min:1']"
          autocomplete="off"
          :add-classes="{
            ElementLayout: {
              innerWrapper: 'border-b border-gray-300 mb-[0.2rem]',
            },
            ElementError: {
              container: 'hidden',
            },
            TextElement: {
              inputContainer: 'border-none',
              input: '!text-xs',
            },
          }"
          :conditions="[['anchor_period_starting_point', '!=', 'today'], ['anchor_period_starting_point', '!=', 'custom_date']]"
          @keydown.space.stop
        />
        <SelectElement
          name="anchor_period_starting_time_unit"
          class="capitalize w-2/3"
          :add-classes="{
            SelectElement: {
              select: {
                container: 'border-none',
                wrapper: 'border-b border-gray-300',
                singleLabelText: 'text-xs',
                option: 'text-xs',
              },
            },
          }"
          :default="props.filterConfig?.anchor_period_starting_time_unit"
          :can-clear="false"
          :can-deselect="false"
          :native="false"
          :conditions="[['anchor_period_starting_point', '!=', 'today'], ['anchor_period_starting_point', '!=', 'custom_date']]"
          :items="['days', 'weeks', 'months', 'quarters', 'years']"
        />
        <SelectElement
          name="anchor_period_starting_point"
          class="capitalize w-2/3"
          :add-classes="{
            SelectElement: {
              select: {
                container: 'border-none',
                wrapper: 'border-b border-gray-300',
                singleLabelText: 'text-xs',
                option: 'text-xs',
              },
            },
          }"
          :native="false"
          :default="props.filterConfig?.anchor_period_starting_point || 'today'"
          :can-clear="false"
          :can-deselect="false"
          :items="[
            { label: 'Today', value: 'today' },
            ...[props.formData?.operator_name !== 'relative_date_next' ? { label: 'Earlier', value: 'earlier' } : null],
            ...[props.formData?.operator_name !== 'relative_date_last' ? { label: 'Later', value: 'later' } : null],
            { label: 'Custom Date', value: 'custom_date' },
          ].filter(item => !!item)"
          @change="onAnchorPeriodStartingPointChange"
        />
        <DateTimeElement
          :options="{
            teleport: false,
            ...componentConfig.options,
            format: 'dd-MM-yyyy',
            clearable: false,
            hideInputIcon: true,
          }"
          class="anchor_custom_period"
          :conditions="[['anchor_period_starting_point', '==', 'custom_date']]"
          :default="filterConfig?.anchor_period_starting_custom_date || null"
          name="anchor_period_starting_custom_date"
        />
      </div>
      <CheckboxElement
        v-if="props.formData?.anchor_period_starting_point === 'today'"
        name="anchor_period_include_period"
        :default="filterConfig?.anchor_period_include_period || false"
        :add-classes="{
          CheckboxElement: {
            wrapper: 'px-1 mt-2 gap-2',
          },
        }"
      >
        {{ include_period_checkbox_text }}
      </CheckboxElement>
    </div>
  </div>
</template>

<style scoped lang="scss">
.anchor_custom_period {
  :deep(.dp__input) {
    border: none;
    @apply px-2 text-xs;
  }
}
.anchor_custom_period {
  :deep(.dp__input_wrap) {
    @apply border-b border-gray-300 mb-1;
  }
}
</style>
